/**
 * ThetaMiner chart strategy — renders the SAME premium-decay signal the auto-engine trades
 * (backend multileg/thetaMinerSignal.ts), as a historical replay of B (entered short) / X (exit)
 * markers on the chart. Designed for the SYNTHETIC option-premium chart: it transforms the bars to
 * CLH internally (so it's correct on any displayed candle type) and colours each bar by CLH
 * direction (red = premium decaying / short-favourable, green = rising).
 *
 * Signal (mirrors the engine): body-low = min(open,close). ENTER when body-low makes a FRESH new
 * low < the previous body-low AND < the MM band lower (BBR / vol-spike staircase). Once short, TRAIL
 * the stop to max(previous two CLH body-highs) after the body-low rises, with an initial hard stop
 * (entry × 1.40); EXIT when the premium breaches it. RE-INIT after a trail exit on a fresh low + red
 * bar (capped). This is a visual replay — no live-dwell (that only matters for a forming live bar).
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { transformCandles } from '../candleTransforms';
import { computeBbrReversals, computeVolumeSpikes } from '../dailyBlastIndicators';
import type { ChartStrategy, StrategyOverlayResult, ChartMarker, CandleColor } from './types';

const HARD_SL_PCT = 40;      // initial hard stop = entry × (1 + 40%)
const MAX_REINITS = 5;
const REINIT_COOLDOWN_BARS = 1;
const UP = '#26a69a', DOWN = '#ef5350';

const bodyLow = (b: CandleRow) => Math.min(b.open, b.close);
const bodyHigh = (b: CandleRow) => Math.max(b.open, b.close);

/** Per-bar MM lower staircase: the body-low of the most recent BBR/vol-spike bar up to each bar. */
function mmLowerSeries(clh: CandleRow[]): number[] {
  const bbr = computeBbrReversals(clh);
  const spikes = computeVolumeSpikes(clh);
  const out: number[] = new Array(clh.length).fill(NaN);
  let cur = NaN;
  for (let i = 0; i < clh.length; i++) {
    const t = clh[i].time as number;
    if (bbr.overboughtAt.has(t) || bbr.oversoldAt.has(t) || spikes.has(t)) cur = bodyLow(clh[i]);
    out[i] = cur;
  }
  return out;
}

export function computeThetaMinerOverlay(candles: CandleRow[]): StrategyOverlayResult {
  if (!candles || candles.length < 6) return { candleColors: [], markers: [] };
  const clh = transformCandles(candles, 'CLH');
  const mm = mmLowerSeries(clh);
  const markers: ChartMarker[] = [];
  const candleColors: CandleColor[] = [];
  const signalsByTime = new Map<number, { signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT'; strength: 'HIGH' | 'MEDIUM' | 'LOW' | null; pos: 'LONG' | 'SHORT' | 'FLAT'; reason?: string | null }>();
  const positionByTime = new Map<number, 'LONG' | 'SHORT' | 'FLAT'>();

  let pos: 'FLAT' | 'SHORT' = 'FLAT';
  let armed = true, entryPremium = 0, trailSl: number | null = null;
  let reinits = 0, lastExitBar = -1, lastExitWasTrail = false;

  for (let i = 1; i < clh.length; i++) {
    const cur = clh[i], prev = clh[i - 1];
    const bl = bodyLow(cur), blPrev = bodyLow(prev);
    const t = cur.time as number;
    // Bar colour by CLH DIRECTION (force both up/down to the same colour so the displayed bar shows
    // the decay regime regardless of its own open/close): red = decaying (short-favourable), green = rising.
    const dirCol = cur.close < cur.open ? DOWN : UP;
    candleColors.push({ time: t, upColor: dirCol, downColor: dirCol });

    if (bl >= blPrev) armed = true; // re-arm the fresh-low latch on any non-new-low

    if (pos === 'SHORT') {
      // Trail: tighten to max(previous two body-highs) once the body-low rises.
      if (bodyLow(cur) > bodyLow(prev) && i >= 2) {
        const cand = Math.max(bodyHigh(clh[i - 1]), bodyHigh(clh[i - 2]));
        trailSl = trailSl == null ? cand : Math.min(trailSl, cand);
      }
      const hardSl = entryPremium * (1 + HARD_SL_PCT / 100);
      const stop = trailSl != null ? Math.min(hardSl, trailSl) : hardSl;
      if (cur.high >= stop) {
        markers.push({ time: t, position: 'aboveBar', text: 'X', color: '#ff9800', shape: 'circle' });
        signalsByTime.set(t, { signal: 'EXIT_SHORT', strength: null, pos: 'FLAT', reason: 'THETA_TRAIL_SL' });
        pos = 'FLAT'; lastExitBar = i; lastExitWasTrail = true; armed = false; trailSl = null;
        positionByTime.set(t, 'FLAT');
        continue;
      }
      positionByTime.set(t, 'SHORT');
      continue;
    }

    // FLAT — look for an entry.
    const freshLow = bl < blPrev && armed;
    const belowMm = Number.isFinite(mm[i]) && bl < mm[i];
    const redBar = cur.close < cur.open;
    const cooldownOk = lastExitBar < 0 || (i - lastExitBar) > REINIT_COOLDOWN_BARS;
    const reinitAllowed = lastExitWasTrail && reinits < MAX_REINITS && i !== lastExitBar && cooldownOk;
    let enter = false, via = '';
    if (freshLow && belowMm) { enter = true; via = 'A'; }
    else if (reinitAllowed && freshLow && redBar) { enter = true; via = 'B'; }

    if (enter) {
      markers.push({ time: t, position: 'belowBar', text: 'TM', color: '#00C853', shape: 'arrowUp', size: 2 });
      signalsByTime.set(t, { signal: 'SHORT', strength: null, pos: 'SHORT', reason: via === 'A' ? 'THETA_FRESH_LOW' : 'THETA_REINIT' });
      pos = 'SHORT'; entryPremium = cur.close; armed = false; trailSl = null;
      if (via === 'B') reinits += 1;
      lastExitWasTrail = false;
      positionByTime.set(t, 'SHORT');
    } else {
      positionByTime.set(t, 'FLAT');
    }
  }

  return { candleColors, markers, signalsByTime, positionByTime };
}

export const thetaMinerStrategy: ChartStrategy = {
  id: 'thetaminer',
  name: 'ThetaMiner',
  computeOverlays(candles: CandleRow[]): StrategyOverlayResult {
    return computeThetaMinerOverlay(candles);
  },
};
